import React, { useState } from 'react';
import AWS from 'aws-sdk';
import { ToastContainer, toast, Slide } from 'react-toastify';
import { Container, TextField, Button, Typography, Box } from '@mui/material';

AWS.config.update({ region: 'eu-west-1' }); // Replace with your region

export default function LoginComponent() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [isConfirmingCode, setIsConfirmingCode] = useState(false);

  const cognito = new AWS.CognitoIdentityServiceProvider();
  const clientId = '4245qg8nfr209bakgtj7vogai4';

  // Function to refresh the token
  const refreshAuthToken = async (refreshToken) => {
    const params = {
      AuthFlow: 'REFRESH_TOKEN_AUTH',
      ClientId: clientId,
      AuthParameters: {
        REFRESH_TOKEN: refreshToken
      }
    };

    try {
      const data = await cognito.initiateAuth(params).promise();
      // Store the new tokens
      localStorage.setItem('accessToken', data.AuthenticationResult.AccessToken);
      localStorage.setItem('idToken', data.AuthenticationResult.IdToken);
      // Note: The refresh token typically doesn't change on refresh
      return true;
    } catch (err) {
      console.error('Error refreshing token:', err);
      // Handle failed token refresh by redirecting to login
      localStorage.removeItem('accessToken');
      localStorage.removeItem('idToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('tokenExpiration');
      return false;
    }
  };

  // Function to check token validity and refresh if needed
  const validateToken = async () => {
    const tokenExpiration = localStorage.getItem('tokenExpiration');
    const refreshToken = localStorage.getItem('refreshToken');

    if (!tokenExpiration || !refreshToken) {
      return false;
    }

    // If token is expired or will expire in the next 5 minutes
    const fiveMinutes = 5 * 60 * 1000;
    if (Date.now() > parseInt(tokenExpiration) - fiveMinutes) {
      return await refreshAuthToken(refreshToken);
    }

    return true;
  };

  // Initialize the token validation on component mount
  React.useEffect(() => {
    // Setup a token validation check every 5 minutes
    const tokenCheckInterval = setInterval(
      () => {
        validateToken();
      },
      5 * 60 * 1000
    );

    // Check token on initial load
    validateToken();

    return () => clearInterval(tokenCheckInterval);
  }, []);

  const signIn = async () => {
    const params = {
      AuthFlow: 'USER_PASSWORD_AUTH',
      ClientId: clientId,
      AuthParameters: { USERNAME: email, PASSWORD: password }
    };

    try {
      const data = await cognito.initiateAuth(params).promise();

      // Store tokens
      localStorage.setItem('accessToken', data.AuthenticationResult.AccessToken);
      localStorage.setItem('idToken', data.AuthenticationResult.IdToken);
      localStorage.setItem('refreshToken', data.AuthenticationResult.RefreshToken);

      // Decode the JWT to get expiration time
      const payload = JSON.parse(atob(data.AuthenticationResult.IdToken.split('.')[1]));
      const expirationTime = payload.exp * 1000; // Convert to milliseconds
      localStorage.setItem('tokenExpiration', expirationTime.toString());

      toast.success('Logged in Successfully');
      window.location.href = '/categories';
    } catch (err) {
      toast.error('Username or Password is incorrect');
    }
  };

  const handleForgotPassword = async () => {
    try {
      await cognito.forgotPassword({ ClientId: clientId, Username: email }).promise();
      toast.info('Verification code sent to your email');
      setIsConfirmingCode(true);
    } catch (err) {
      toast.error('Error sending reset code');
    }
  };

  const handleConfirmForgotPassword = async () => {
    try {
      await cognito
        .confirmForgotPassword({
          ClientId: clientId,
          Username: email,
          ConfirmationCode: verificationCode,
          Password: newPassword
        })
        .promise();
      toast.success('Password reset successfully. You can now log in.');
      setIsForgotPassword(false);
      setIsConfirmingCode(false);
    } catch (err) {
      toast.error('Error resetting password');
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 14, textAlign: 'center' }}>
      <Typography variant="h4" gutterBottom>
        {isForgotPassword ? (isConfirmingCode ? 'Reset Password' : 'Forgot Password') : 'Sign In'}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label="Email"
          variant="outlined"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
        />
        {isForgotPassword ? (
          isConfirmingCode ? (
            <>
              <TextField
                label="Verification Code"
                variant="outlined"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                fullWidth
              />
              <TextField
                label="New Password"
                variant="outlined"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                fullWidth
              />
              <Button variant="contained" color="primary" onClick={handleConfirmForgotPassword}>
                Confirm Reset
              </Button>
            </>
          ) : (
            <Button variant="contained" color="primary" onClick={handleForgotPassword}>
              Send Verification Code
            </Button>
          )
        ) : (
          <>
            <TextField
              label="Password"
              variant="outlined"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
            />
            <Button variant="contained" color="primary" onClick={signIn}>
              Sign In
            </Button>
            <Button color="secondary" onClick={() => setIsForgotPassword(true)}>
              Forgot Password?
            </Button>
          </>
        )}
        {isForgotPassword && (
          <Button
            color="secondary"
            onClick={() => {
              setIsForgotPassword(false);
              setIsConfirmingCode(false);
            }}>
            Back to Sign In
          </Button>
        )}
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick={false}
          rtl={false}
          pauseOnFocusLoss={false}
          draggable={false}
          pauseOnHover={false}
          theme="colored"
          transition={Slide}
        />
      </Box>
    </Container>
  );
}
